/**
 * How a vendor update reads to the org it is proposed to.
 *
 * Three words, because there are only three things a reader can do about an
 * update: it is waiting on someone, it landed, or it is not going ahead.
 *
 * The seven database statuses are our pipeline's vocabulary, not theirs.
 * "pending review", "approved", "draft" and "partially applied" all mean the
 * same thing to a tenant: nothing has happened yet and it still might, while
 * telling them apart requires knowing how the rollout works.
 *
 * Shared by the Pending updates list and the Health page's badge so the two
 * cannot disagree about what is still open.
 */
export const PROPOSAL_STATUS: Record<string, { label: string; variant: "failed" | "info" | "active" | "draft"; title?: string }> = {
  draft: { label: "Open", variant: "info" },
  pending_review: { label: "Open", variant: "info" },
  approved: { label: "Open", variant: "info" },
  partially_applied: { label: "Open", variant: "failed", title: "Part of this was applied and part failed" },
  applied: { label: "Applied", variant: "active" },
  rejected: { label: "Rejected", variant: "draft" },
  superseded: { label: "Rejected", variant: "draft", title: "Replaced by a newer update" },
};

export const proposalStatusOf = (s: string) => PROPOSAL_STATUS[s] ?? { label: "Open", variant: "info" as const };

/** Resolved means nobody has to do anything: it landed, or it was refused. */
export const RESOLVED_PROPOSAL_STATUSES: ReadonlySet<string> = new Set(
  Object.entries(PROPOSAL_STATUS).filter(([, v]) => v.label !== "Open").map(([k]) => k),
);

export const isOpenProposalStatus = (s: string): boolean => !RESOLVED_PROPOSAL_STATUSES.has(s);
